/**
 * EVERY NUMBER THE SOUND IS MADE OF, in one file, so that a change to what the game sounds
 * like is a diff a reader can hold in their head.
 *
 * -- WHY THE CONSTANTS LIVE APART FROM THE GENERATORS ----------------------------
 *
 * Three reasons, and only the first is tidiness.
 *
 *   1. DETERMINISM IS AUDITABLE HERE. Every seed in this lane is written down in this file and
 *      nowhere else. Nothing reads `Math.random`, nothing reads the clock, nothing reads the
 *      save. Two renders of this lane on two machines produce the same Float32 samples, which
 *      is what the run's hash pinning requires, and the proof of it is that this file is the
 *      only source of entropy and it is a list of literals.
 *
 *   2. THE LEVELS ARE THE DELIVERED LEVELS. Each layer is normalised to the RMS written here
 *      and the master gain is 1, so a figure in this file is a figure a meter reads off the
 *      output rather than a figure some later stage scales. `measure.ts` checks exactly that.
 *
 *   3. EVERY ONE OF THESE IS A GUESS UNTIL SOMEBODY LISTENS. The spectra below are set from
 *      published descriptions of distant surf - broad energy under about 1 kHz, a roll-off
 *      above it, no hiss - and from the arithmetic of what does not fatigue. That is a
 *      MODELLED starting point, not a measured one, and the owner tunes it by ear. Keeping
 *      them together is what makes that a five-minute job.
 *
 * -- THE TWO LENGTHS, AND WHY THEY ARE COPRIME -----------------------------------
 *
 * The swell buffer is 17 seconds and the surface buffer is 11. Both loop seamlessly on their
 * own (see filters.ts), so the only thing left to give away that a loop exists is RECOGNISING
 * the pattern - and a pattern is recognisable long before a click is. Two loops whose lengths
 * share no factor drift against each other and only line up again at their product: 187
 * seconds, three minutes and seven, before the pair has repeated once. Nobody identifies a
 * three-minute period in moving water. Two 12-second loops would realign every 12 seconds and
 * the whole exercise would be wasted.
 */

/**
 * SEEDS. Each one names the stream it feeds, and each is a literal so a regenerated artifact
 * hashes to the same bytes. Changing one changes what the game sounds like; that is the point,
 * and it is the only knob in this lane with no other effect.
 */
export const SEEDS = {
  swell: 0x53_57_45_4c,
  surfaceLowLeft: 0x53_46_4c_4c,
  surfaceLowRight: 0x53_46_4c_52,
  surfaceHighLeft: 0x53_46_48_4c,
  surfaceHighRight: 0x53_46_48_52,
  moments: 0x4d_4f_4d_54,
} as const;

/**
 * THE SWELL: everything under about 120 Hz, the part of the sea you feel rather than hear.
 *
 * Mono on purpose. Below roughly 200 Hz a listener cannot localise a source, so two decorrelated
 * low channels buy no width and cost real memory - and on headphones they can read as pressure
 * rather than as space. One channel, sent to both.
 */
export const SWELL = {
  seconds: 17,
  /** Two poles, so it falls 12 dB per octave above this and reaches nothing that hisses. */
  lowpassHz: 110,
  lowpassPoles: 2,
  /** Below this is cost without sound: no laptop speaker reproduces it and it eats headroom. */
  highpassHz: 28,
  /** Slow, uneven breathing. 1 cycle per buffer dominates; 2, 3 and 5 keep it from being a sine. */
  envelopePartials: [1, 2, 3, 5],
  envelopePhases: [0.0, 1.97, 3.41, 5.12],
  envelopeDepth: 0.55,
  /** -35 dBFS. Under the surface layer, because a bed you can hear the bottom of is a hum. */
  rms: 0.0178,
} as const;

/**
 * THE SURFACE: the part you actually hear, and it is split into two bands that breathe
 * INDEPENDENTLY.
 *
 * That split is the one idea in this file that is doing real work. A single noise bed with one
 * amplitude envelope is a fan being turned up and down - every frequency rises and falls
 * together, which is exactly what a machine does and exactly what water does not. Moving water
 * modulates its high band much faster and much less regularly than its low band, and that
 * difference is the cue a listener uses to tell surf from an air conditioner without ever
 * thinking about it. Two bands, two envelopes with different partials and different phases, and
 * the bed stops sounding like equipment.
 *
 * Both bands are stereo from independent noise, filtered identically, so the image is wide and
 * the spectrum is the same on both sides.
 */
export const SURFACE = {
  seconds: 11,
  low: {
    /** The body. Rolls off 12 dB per octave above this. */
    lowpassHz: 900,
    lowpassPoles: 2,
    highpassHz: 150,
    envelopePartials: [1, 2, 3, 5],
    envelopePhases: [0.83, 2.61, 4.02, 0.44],
    envelopeDepth: 0.42,
    gain: 1,
  },
  high: {
    /** The wash. Capped hard: above about 5 kHz a noise bed is hiss and hiss is what fatigues. */
    lowpassHz: 3400,
    lowpassPoles: 2,
    highpassHz: 700,
    /** Faster and more irregular than the body - this is the band that says "water". */
    envelopePartials: [2, 3, 5, 8, 13],
    envelopePhases: [1.11, 3.77, 0.29, 5.63, 2.18],
    envelopeDepth: 0.62,
    /** Well under the body. A wash you can pick out as a separate sound is a hiss. */
    gain: 0.34,
  },
  /** -30.5 dBFS for the summed pair. The bed a landing has to be heard over. */
  rms: 0.0299,
} as const;

/** Master fade in on unlock, and out on quiet. Seconds. */
export const FADE = { in: 2.2, out: 0.28 } as const;

/**
 * ONE-SHOT LEVELS, as peak sample values. Each is a deliberate distance above the bed:
 * the bed sits at about -30 dBFS RMS, so a landing at -15 dBFS peak is heard without being
 * an event, and a record at -13 is heard as one.
 */
export const MOMENT_PEAK = {
  /** Every few seconds while fishing, forever. It has to be small. */
  land: 0.17,
  /** The heaviest fish of its species. Same gesture, more of it. */
  landHeavy: 0.29,
  /** A personal best. Rare enough to be allowed to be a sound. */
  record: 0.21,
  /** Not a fish. Duller and shorter, so it reads as a disappointment without being one. */
  junk: 0.1,
  /** The sheet coming up, and going back down. */
  lift: 0.13,
  settle: 0.11,
  /** A choice written on the paper. */
  mark: 0.075,
} as const;

/**
 * HOW OFTEN A LANDING MAY BE HEARD, in real milliseconds - AND IT CURRENTLY NEVER FIRES.
 *
 * This constant was written to solve a problem the game turns out not to have, and the honest
 * thing is to say so beside it rather than quietly leave a mechanism that does nothing.
 *
 * MEASURED on the live engine over a real hour per row (`cadence.test.ts`, seed 4242):
 *
 *     level  1, skill   0    537 landings/hour    one every  6.7 s
 *     level  1, skill 100    904 landings/hour    one every  4.0 s
 *     level 50, skill   0    282 landings/hour    one every 12.8 s
 *     level 99, skill 100    287 landings/hour    one every 12.5 s
 *
 * The fastest state the game can reach is a landing every four seconds, and the realistic early
 * game is one every seven. A soft sound at that spacing is the reward, not a drip - which was
 * the fear this number was written against, and the measurement refutes it.
 *
 * So it stays as a GUARD rather than as a mechanism: 0% of landings are suppressed under the
 * current balance, and `cadence.test.ts` asserts exactly that, so the day a balance change takes
 * casts under 1.4 seconds the fence goes red and somebody has to decide what a landing should
 * sound like at that rate. That is worth more than deleting it and rediscovering the question.
 */
export const LAND_MIN_GAP_MS = 1400;

/**
 * MORE LANDINGS THAN THIS BETWEEN TWO LOOKS AND THE SAVE SETTLED WHILE NOBODY WAS THERE.
 *
 * The same rule and the same number the catch mark already uses in world-entry.ts, deliberately
 * duplicated rather than shared, because it is answering a different question with the same
 * answer: the mark is a witness rather than a receipt, and so is the sound. Twenty landings
 * arriving at once because a tab was shut for an hour is a bookkeeping event, and bookkeeping
 * is silent.
 */
export const SETTLE_BURST_MAX = 4;

/** Where the mute preference lives. Versioned like every other key on this page. */
export const MUTE_KEY = 'reelin.audio.muted.v1';
/** Set once the player has been told which key quiets it, so the line never nags. */
export const TOLD_KEY = 'reelin.audio.told.v1';
